import * as React from "react"
import { Head, router, usePage } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { InlineField } from "@/components/ui/inline-edit"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { useInlineRecord } from "@/hooks/use-inline-record"
import { inlinePatch } from "@/lib/inline-save"
import type { PageProps } from "@/types/inertia"
import { EmailLayout, type EmailNavProps } from "./EmailNav"

type Setting = {
  enabled: boolean
  provider: string
  from_address: string
  from_name: string
  deliver_to: string
  smtp_address: string
  smtp_port: string
  smtp_username: string
  smtp_password: string
  password_set: boolean
  starttls: boolean
}

type Provider = {
  key: string
  label: string
  address: string | null
  port: number
  starttls: boolean
}

type Environment = {
  method: string
  address: string
  port: string
  username: string
  from: string
  deliver_to: string
}

type Props = PageProps & {
  setting: Setting
  providers: Provider[]
  environment: Environment
  email_nav: EmailNavProps
}

const URL = "/admin/email"
const PARAM = "mail_setting"

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset className="mt-6 rounded-lg border border-hairline bg-white p-5">
      <legend className="px-1">{title}</legend>
      <div className="grid gap-5 sm:grid-cols-2">{children}</div>
    </fieldset>
  )
}

// The password never comes back from the server, so it cannot ride on
// useInlineRecord (whose draft is reconciled against the props). A local
// draft with its own tick: save sends it, then the box empties again.
function usePasswordField() {
  const [value, setValue] = React.useState("")
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const save = () => {
    if (saving || value === "") return
    setSaving(true)
    setError(null)
    inlinePatch(URL, { [PARAM]: { smtp_password: value } })
      .then(() => setValue(""))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Could not save."))
      .finally(() => setSaving(false))
  }
  const cancel = () => {
    setValue("")
    setError(null)
  }
  const keys: React.KeyboardEventHandler = (e) => {
    if (e.key === "Enter") {
      e.preventDefault()
      save()
    } else if (e.key === "Escape") {
      e.preventDefault()
      cancel()
    }
  }
  return { value, set: setValue, state: { dirty: value !== "", saving, error, save, cancel }, keys }
}

export default function Show() {
  const props = usePage<Props>().props
  const { setting, providers, environment, email_nav, errors, flash } = props
  const fields = useInlineRecord<Setting>({ record: setting, url: URL, param: PARAM })
  const v = fields.values
  const password = usePasswordField()
  const [testTo, setTestTo] = React.useState("")
  const [testing, setTesting] = React.useState(false)

  const fromState = fields.state("from_address")
  const fromNameState = fields.state("from_name")
  const deliverToState = fields.state("deliver_to")
  // Picking a provider fills the server fields; they travel with it so one
  // tick commits the set rather than leaving three boxes each waiting on
  // their own.
  const providerState = fields.state("provider", {
    with: (d) => ({ smtp_address: d.smtp_address, smtp_port: d.smtp_port, starttls: d.starttls }),
  })
  const addressState = fields.state("smtp_address")
  const portState = fields.state("smtp_port")
  const starttlsState = fields.state("starttls")
  const usernameState = fields.state("smtp_username")

  function pickProvider(key: string) {
    const preset = providers.find((p) => p.key === key)
    if (!preset) return
    fields.patch({
      provider: key,
      ...(preset.address ? { smtp_address: preset.address } : {}),
      smtp_port: String(preset.port),
      starttls: preset.starttls,
    })
  }

  function sendTest() {
    setTesting(true)
    router.post(
      `${URL}/test`,
      { to: testTo },
      { preserveScroll: true, onFinish: () => setTesting(false) },
    )
  }

  const inUse = setting.enabled
    ? `${setting.smtp_address || "—"} as ${setting.from_address || "—"}`
    : environment.method === "smtp"
      ? `${environment.address} as ${environment.from || environment.username}`
      : environment.method === "test"
        ? "nothing — this environment swallows mail"
        : `${environment.method} (server configuration)`

  return (
    <AdminShell>
      <Head title="Email">
        <meta name="description" content="How the website sends email and where enquiries are delivered." />
        <meta property="og:title" content="Email" />
        <meta property="og:description" content="How the website sends email and where enquiries are delivered." />
      </Head>

      <PageHeader
        title="Email"
        description="How the website sends email and where enquiries land. Each box saves on its own tick; nothing here is used until the switch below is on."
      />

      {flash?.notice && <p className="mt-4 text-sm text-accent">{flash.notice}</p>}
      {flash?.alert && <p className="mt-4 text-sm text-danger">{flash.alert}</p>}

      <EmailLayout nav={email_nav}>
      <div className="flex flex-wrap items-center gap-4 rounded-lg border border-hairline bg-white px-5 py-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-ink-display">Use these settings</span>
            <Badge tone={setting.enabled ? "accent" : "neutral"}>{setting.enabled ? "On" : "Off"}</Badge>
          </div>
          <p className="mt-1 text-sm text-ink-muted">
            Sending now via <strong>{inUse}</strong>.
            {!setting.enabled && " That is the server's configuration; switch on to use the settings below instead."}
          </p>
        </div>
        <Switch
          checked={setting.enabled}
          onChange={(enabled) => router.patch(URL, { [PARAM]: { enabled } }, { preserveScroll: true })}
          aria-label="Use these settings"
        />
      </div>

      <Section title="Sender details">
        <InlineField label="Email address" htmlFor="from_address" error={errors.from_address} state={fromState}
          hint="What outbound mail is sent as. Gmail rewrites this to the signed-in account unless it is a configured alias.">
          <Input id="from_address" type="email" value={v.from_address}
            onChange={(e) => fields.set("from_address", e.target.value)} onKeyDown={fromState.keys}
            placeholder={environment.from || "you@example.com"} aria-invalid={!!errors.from_address} />
        </InlineField>
        <InlineField label="Display name" htmlFor="from_name" error={errors.from_name} state={fromNameState}>
          <Input id="from_name" value={v.from_name}
            onChange={(e) => fields.set("from_name", e.target.value)} onKeyDown={fromNameState.keys}
            placeholder="Sanctuary Therapy Centre" />
        </InlineField>
        <InlineField label="Deliver enquiries to" htmlFor="deliver_to" error={errors.deliver_to} state={deliverToState}
          hint="Every enquiry, callback, viewing and booking notification, on both websites." className="sm:col-span-2">
          <Input id="deliver_to" type="email" value={v.deliver_to}
            onChange={(e) => fields.set("deliver_to", e.target.value)} onKeyDown={deliverToState.keys}
            placeholder={environment.deliver_to} aria-invalid={!!errors.deliver_to} />
        </InlineField>
      </Section>

      <Section title="Server details">
        <InlineField label="Email provider" htmlFor="provider" error={errors.provider} state={providerState}>
          <Select id="provider" value={v.provider} onChange={(e) => pickProvider(e.target.value)}>
            {providers.map((p) => (
              <option key={p.key} value={p.key}>{p.label}</option>
            ))}
          </Select>
        </InlineField>
        <InlineField label="Server name (SMTP host)" htmlFor="smtp_address" error={errors.smtp_address} state={addressState}>
          <Input id="smtp_address" value={v.smtp_address}
            onChange={(e) => fields.set("smtp_address", e.target.value)} onKeyDown={addressState.keys}
            placeholder="smtp.gmail.com" aria-invalid={!!errors.smtp_address} />
        </InlineField>
        <InlineField label="Port" htmlFor="smtp_port" error={errors.smtp_port} state={portState}>
          <Input id="smtp_port" type="number" inputMode="numeric" min={1} max={65535} value={v.smtp_port}
            onChange={(e) => fields.set("smtp_port", e.target.value)} onKeyDown={portState.keys}
            aria-invalid={!!errors.smtp_port} className="max-w-[8rem]" />
        </InlineField>
        <InlineField label="Connection" htmlFor="starttls" state={starttlsState}>
          <label htmlFor="starttls" className="flex items-center gap-2 font-normal text-ink-body">
            <Checkbox id="starttls" checked={v.starttls} onChange={(e) => fields.set("starttls", e.target.checked)} />
            Use secure connection (STARTTLS)
          </label>
        </InlineField>
      </Section>

      <Section title="Login details">
        <InlineField label="Username" htmlFor="smtp_username" error={errors.smtp_username} state={usernameState}
          hint="For Gmail, the full address.">
          <Input id="smtp_username" type="email" value={v.smtp_username}
            onChange={(e) => fields.set("smtp_username", e.target.value)} onKeyDown={usernameState.keys}
            placeholder={environment.username || "you@gmail.com"} aria-invalid={!!errors.smtp_username} />
        </InlineField>
        <InlineField label="Password" htmlFor="smtp_password" error={errors.smtp_password} state={password.state}
          hint={setting.password_set
            ? "A password is set. Type a new one to replace it; it is never shown again."
            : "For Gmail, an app password — Google Account → Security → 2-Step Verification → App passwords."}>
          <Input id="smtp_password" type="password" autoComplete="new-password" value={password.value}
            onChange={(e) => password.set(e.target.value)} onKeyDown={password.keys}
            placeholder={setting.password_set ? "••••••••••••" : ""} aria-invalid={!!errors.smtp_password} />
        </InlineField>
      </Section>

      <div className="mt-6 rounded-lg border border-hairline bg-white p-5">
        <h2>Send a test email</h2>
        <p className="mt-1 text-sm text-ink-muted">
          Goes through exactly the path an enquiry would — the settings above if they are switched on, the server's configuration if not. The result is reported here.
        </p>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div className="min-w-64 flex-1">
            <label htmlFor="test_to">Send to</label>
            <Input id="test_to" type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)}
              placeholder={v.deliver_to || environment.deliver_to} className="mt-1" />
          </div>
          <Button type="button" onClick={sendTest} disabled={testing}>
            {testing ? "Sending…" : "Send test email"}
          </Button>
        </div>
      </div>
      </EmailLayout>
    </AdminShell>
  )
}
